import { Toast as KToast, toaster } from '@kobalte/core/toast'
import { CheckCircle2, XCircle, AlertCircle, Info, X } from 'lucide-solid'
import type { JSX } from 'solid-js'
import { Portal } from 'solid-js/web'

type Tone = 'success' | 'danger' | 'warn' | 'info' | 'neutral'

const iconFor = (t: Tone) => ({
  success: CheckCircle2, danger: XCircle, warn: AlertCircle, info: Info, neutral: Info,
})[t]

function showToastByTone(tone: Tone, message: string) {
  toaster.show((props) => (
    <KToast
      toastId={props.toastId}
      class={
        'flex gap-3 items-start bg-surface border border-border rounded-lg shadow-md p-4 min-w-72 ' +
        'data-[opened]:animate-in data-[opened]:slide-in-from-right ' +
        'data-[closed]:animate-out data-[closed]:slide-out-to-right duration-base'
      }
    >
      {(() => {
        const Icon = iconFor(tone)
        const color = { success: 'text-success', danger: 'text-danger', warn: 'text-warn', info: 'text-info', neutral: 'text-muted' }[tone]
        return <Icon size={20} class={color} />
      })()}
      <div class="flex-1 text-sm text-fg">{message}</div>
      <KToast.CloseButton class="text-muted hover:text-fg"><X size={16} /></KToast.CloseButton>
    </KToast>
  ))
}

export const toast = {
  success: (msg: string) => showToastByTone('success', msg),
  error: (msg: string) => showToastByTone('danger', msg),
  warn: (msg: string) => showToastByTone('warn', msg),
  info: (msg: string) => showToastByTone('info', msg),
}

export function showToast(message: string, variant: 'success' | 'error' = 'success'): void {
  if (variant === 'error') toast.error(message)
  else toast.success(message)
}

export const ToastList = () => (
  <KToast.List class="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 flex flex-col gap-2 z-[100] outline-none" />
)

export const ToastRegion = (props: { children?: JSX.Element }) => (
  <Portal>
    <KToast.Region>
      {props.children ?? <ToastList />}
    </KToast.Region>
  </Portal>
)

export { KToast as Toast }
